/**
 * MITRE ATT&CK Tactics and Techniques
 * Static snapshot for risk assessment - covers most common enterprise techniques
 * Reference: https://attack.mitre.org/
 */
/**
 * ATT&CK Tactics (Kill Chain phases)
 */
export const ATTACK_TACTICS = [
    {
        id: 'TA0001',
        name: 'Initial Access',
        shortName: 'initial-access',
        description: 'Techniques that use various entry vectors to gain their initial foothold within a network.',
    },
    {
        id: 'TA0002',
        name: 'Execution',
        shortName: 'execution',
        description: 'Techniques that result in adversary-controlled code running on a local or remote system.',
    },
    {
        id: 'TA0003',
        name: 'Persistence',
        shortName: 'persistence',
        description: 'Techniques that adversaries use to keep access to systems across restarts, changed credentials, and other interruptions.',
    },
    {
        id: 'TA0004',
        name: 'Privilege Escalation',
        shortName: 'privilege-escalation',
        description: 'Techniques that adversaries use to gain higher-level permissions on a system or network.',
    },
    {
        id: 'TA0005',
        name: 'Defense Evasion',
        shortName: 'defense-evasion',
        description: 'Techniques that adversaries use to avoid detection throughout their compromise.',
    },
    {
        id: 'TA0006',
        name: 'Credential Access',
        shortName: 'credential-access',
        description: 'Techniques for stealing credentials like account names and passwords.',
    },
    {
        id: 'TA0007',
        name: 'Discovery',
        shortName: 'discovery',
        description: 'Techniques an adversary may use to gain knowledge about the system and internal network.',
    },
    {
        id: 'TA0008',
        name: 'Lateral Movement',
        shortName: 'lateral-movement',
        description: 'Techniques that adversaries use to enter and control remote systems on a network.',
    },
    {
        id: 'TA0009',
        name: 'Collection',
        shortName: 'collection',
        description: 'Techniques adversaries may use to gather information relevant to their goal.',
    },
    {
        id: 'TA0010',
        name: 'Exfiltration',
        shortName: 'exfiltration',
        description: 'Techniques that adversaries may use to steal data from your network.',
    },
    {
        id: 'TA0011',
        name: 'Command and Control',
        shortName: 'command-and-control',
        description: 'Techniques that adversaries may use to communicate with systems under their control.',
    },
    {
        id: 'TA0040',
        name: 'Impact',
        shortName: 'impact',
        description: 'Techniques that adversaries use to disrupt availability or compromise integrity.',
    },
];
/**
 * ATT&CK Techniques - Common enterprise techniques relevant to CMDB components
 * This is a curated subset focused on cloud/infrastructure risks
 */
export const ATTACK_TECHNIQUES = [
    // Initial Access
    {
        id: 'T1566',
        name: 'Phishing',
        tacticIds: ['TA0001'],
        description: 'Adversaries may send phishing messages to gain access to victim systems.',
        mitigations: ['D3-SU', 'D3-MFA'],
        detection: ['Email filtering', 'User awareness training'],
    },
    {
        id: 'T1566.001',
        name: 'Spearphishing Attachment',
        tacticIds: ['TA0001'],
        description: 'Adversaries may send spearphishing emails with a malicious attachment.',
        mitigations: ['D3-SU', 'D3-DA'],
        detection: ['Attachment analysis', 'Sandboxing'],
    },
    {
        id: 'T1190',
        name: 'Exploit Public-Facing Application',
        tacticIds: ['TA0001'],
        description: 'Adversaries may attempt to exploit a weakness in an Internet-facing host or system.',
        mitigations: ['D3-PSEP', 'D3-NI'],
        detection: ['WAF logs', 'IDS/IPS alerts'],
    },
    {
        id: 'T1078',
        name: 'Valid Accounts',
        tacticIds: ['TA0001', 'TA0003', 'TA0004', 'TA0005'],
        description: 'Adversaries may obtain and abuse credentials of existing accounts.',
        mitigations: ['D3-MFA', 'D3-AZBA'],
        detection: ['Authentication logs', 'Impossible travel'],
    },
    {
        id: 'T1133',
        name: 'External Remote Services',
        tacticIds: ['TA0001', 'TA0003'],
        description: 'Adversaries may leverage external-facing remote services to gain access.',
        mitigations: ['D3-MFA', 'D3-NI'],
        detection: ['VPN logs', 'Remote access logs'],
    },
    // Execution
    {
        id: 'T1059',
        name: 'Command and Scripting Interpreter',
        tacticIds: ['TA0002'],
        description: 'Adversaries may abuse command and script interpreters to execute commands.',
        mitigations: ['D3-ACL', 'D3-EAL'],
        detection: ['Command-line logging', 'Script block logging'],
    },
    {
        id: 'T1203',
        name: 'Exploitation for Client Execution',
        tacticIds: ['TA0002'],
        description: 'Adversaries may exploit software vulnerabilities in client applications.',
        mitigations: ['D3-PSEP', 'D3-SU'],
        detection: ['Endpoint detection', 'Application logs'],
    },
    // Persistence
    {
        id: 'T1136',
        name: 'Create Account',
        tacticIds: ['TA0003'],
        description: 'Adversaries may create an account to maintain access.',
        mitigations: ['D3-AZBA', 'D3-UAM'],
        detection: ['Account creation events', 'Audit logs'],
    },
    {
        id: 'T1098',
        name: 'Account Manipulation',
        tacticIds: ['TA0003', 'TA0004'],
        description: 'Adversaries may manipulate accounts to maintain and/or elevate access.',
        mitigations: ['D3-AZBA', 'D3-MFA'],
        detection: ['Account change events', 'Permission changes'],
    },
    // Privilege Escalation
    {
        id: 'T1068',
        name: 'Exploitation for Privilege Escalation',
        tacticIds: ['TA0004'],
        description: 'Adversaries may exploit software vulnerabilities to elevate privileges.',
        mitigations: ['D3-PSEP', 'D3-PLM'],
        detection: ['System logs', 'Vulnerability scanning'],
    },
    {
        id: 'T1548',
        name: 'Abuse Elevation Control Mechanism',
        tacticIds: ['TA0004', 'TA0005'],
        description: 'Adversaries may circumvent mechanisms designed to control elevated privileges.',
        mitigations: ['D3-UAM', 'D3-PLM'],
        detection: ['UAC events', 'Sudo logs'],
    },
    // Defense Evasion
    {
        id: 'T1562',
        name: 'Impair Defenses',
        tacticIds: ['TA0005'],
        description: 'Adversaries may maliciously modify components to hinder or disable defensive mechanisms.',
        mitigations: ['D3-EAL', 'D3-FAPA'],
        detection: ['Security tool logs', 'Configuration changes'],
    },
    {
        id: 'T1070',
        name: 'Indicator Removal',
        tacticIds: ['TA0005'],
        description: 'Adversaries may delete or modify artifacts generated within systems.',
        mitigations: ['D3-RAPA', 'D3-EAL'],
        detection: ['Log integrity monitoring', 'Audit trails'],
    },
    // Credential Access
    {
        id: 'T1110',
        name: 'Brute Force',
        tacticIds: ['TA0006'],
        description: 'Adversaries may use brute force techniques to gain access to accounts.',
        mitigations: ['D3-MFA', 'D3-APTS'],
        detection: ['Failed login attempts', 'Account lockout events'],
    },
    {
        id: 'T1552',
        name: 'Unsecured Credentials',
        tacticIds: ['TA0006'],
        description: 'Adversaries may search compromised systems to find insecurely stored credentials.',
        mitigations: ['D3-CH', 'D3-SE'],
        detection: ['File access auditing', 'Secret scanning'],
    },
    {
        id: 'T1555',
        name: 'Credentials from Password Stores',
        tacticIds: ['TA0006'],
        description: 'Adversaries may search for common password storage locations.',
        mitigations: ['D3-CH', 'D3-AZBA'],
        detection: ['Password manager access', 'Keychain access'],
    },
    {
        id: 'T1528',
        name: 'Steal Application Access Token',
        tacticIds: ['TA0006'],
        description: 'Adversaries can steal application access tokens.',
        mitigations: ['D3-TER', 'D3-CH'],
        detection: ['Token usage anomalies', 'API access logs'],
    },
    // Discovery
    {
        id: 'T1087',
        name: 'Account Discovery',
        tacticIds: ['TA0007'],
        description: 'Adversaries may attempt to get a listing of accounts.',
        mitigations: ['D3-ACL', 'D3-NI'],
        detection: ['Directory queries', 'LDAP queries'],
    },
    {
        id: 'T1046',
        name: 'Network Service Discovery',
        tacticIds: ['TA0007'],
        description: 'Adversaries may attempt to get a listing of services.',
        mitigations: ['D3-NI', 'D3-SMBA'],
        detection: ['Port scans', 'Network traffic analysis'],
    },
    // Lateral Movement
    {
        id: 'T1021',
        name: 'Remote Services',
        tacticIds: ['TA0008'],
        description: 'Adversaries may use valid accounts to log into remote services.',
        mitigations: ['D3-MFA', 'D3-NI'],
        detection: ['Remote login events', 'RDP/SSH logs'],
    },
    {
        id: 'T1550',
        name: 'Use Alternate Authentication Material',
        tacticIds: ['TA0005', 'TA0008'],
        description: 'Adversaries may use alternate authentication material to bypass authentication.',
        mitigations: ['D3-TER', 'D3-MFA'],
        detection: ['Token reuse detection', 'Pass-the-hash detection'],
    },
    // Collection
    {
        id: 'T1530',
        name: 'Data from Cloud Storage',
        tacticIds: ['TA0009'],
        description: 'Adversaries may access data from cloud storage to collect sensitive data.',
        mitigations: ['D3-ACL', 'D3-DE'],
        detection: ['Cloud storage access logs', 'Data access patterns'],
    },
    {
        id: 'T1213',
        name: 'Data from Information Repositories',
        tacticIds: ['TA0009'],
        description: 'Adversaries may leverage information repositories to mine valuable information.',
        mitigations: ['D3-ACL', 'D3-UAM'],
        detection: ['Repository access logs', 'Search queries'],
    },
    // Exfiltration
    {
        id: 'T1567',
        name: 'Exfiltration Over Web Service',
        tacticIds: ['TA0010'],
        description: 'Adversaries may use an existing, legitimate external Web service to exfiltrate data.',
        mitigations: ['D3-NTA', 'D3-DNM'],
        detection: ['Outbound traffic analysis', 'Data loss prevention'],
    },
    {
        id: 'T1048',
        name: 'Exfiltration Over Alternative Protocol',
        tacticIds: ['TA0010'],
        description: 'Adversaries may steal data by exfiltrating it over a different protocol.',
        mitigations: ['D3-NTA', 'D3-NI'],
        detection: ['Protocol analysis', 'DNS tunneling detection'],
    },
    // Command and Control
    {
        id: 'T1071',
        name: 'Application Layer Protocol',
        tacticIds: ['TA0011'],
        description: 'Adversaries may communicate using OSI application layer protocols.',
        mitigations: ['D3-NTA', 'D3-NI'],
        detection: ['Protocol inspection', 'Traffic analysis'],
    },
    {
        id: 'T1105',
        name: 'Ingress Tool Transfer',
        tacticIds: ['TA0011'],
        description: 'Adversaries may transfer tools or files from an external system.',
        mitigations: ['D3-NI', 'D3-MFA'],
        detection: ['Download detection', 'File integrity monitoring'],
    },
    // Impact
    {
        id: 'T1486',
        name: 'Data Encrypted for Impact',
        tacticIds: ['TA0040'],
        description: 'Adversaries may encrypt data on target systems to interrupt availability.',
        mitigations: ['D3-BA', 'D3-DE'],
        detection: ['Ransomware detection', 'Encryption anomalies'],
    },
    {
        id: 'T1489',
        name: 'Service Stop',
        tacticIds: ['TA0040'],
        description: 'Adversaries may stop or disable services to render them unavailable.',
        mitigations: ['D3-RAPA', 'D3-PM'],
        detection: ['Service status monitoring', 'Process monitoring'],
    },
    {
        id: 'T1485',
        name: 'Data Destruction',
        tacticIds: ['TA0040'],
        description: 'Adversaries may destroy data and files on specific systems.',
        mitigations: ['D3-BA', 'D3-RAPA'],
        detection: ['File deletion monitoring', 'Data integrity checks'],
    },
    {
        id: 'T1499',
        name: 'Endpoint Denial of Service',
        tacticIds: ['TA0040'],
        description: 'Adversaries may perform DoS attacks to degrade or block availability.',
        mitigations: ['D3-NTA', 'D3-PM'],
        detection: ['Resource monitoring', 'Traffic anomalies'],
    },
    // Container-specific techniques
    {
        id: 'T1525',
        name: 'Implant Internal Image',
        tacticIds: ['TA0003'],
        description: 'Adversaries may implant cloud or container images with malicious code to establish persistence.',
        mitigations: ['D3-SCF', 'D3-BA'],
        detection: ['Image scanning', 'Registry monitoring'],
    },
    {
        id: 'T1610',
        name: 'Deploy Container',
        tacticIds: ['TA0002', 'TA0005'],
        description: 'Adversaries may deploy a container into an environment to facilitate execution or evade defenses.',
        mitigations: ['D3-ACL', 'D3-PM'],
        detection: ['Container runtime monitoring', 'Orchestrator logs'],
    },
    {
        id: 'T1611',
        name: 'Escape to Host',
        tacticIds: ['TA0004'],
        description: 'Adversaries may break out of a container to gain access to the underlying host.',
        mitigations: ['D3-SCF', 'D3-PSEP'],
        detection: ['Host-based monitoring', 'Container isolation alerts'],
    },
    {
        id: 'T1612',
        name: 'Build Image on Host',
        tacticIds: ['TA0005'],
        description: 'Adversaries may build a container image directly on a host to bypass image scanning.',
        mitigations: ['D3-ACL', 'D3-SCF'],
        detection: ['Build process monitoring', 'Image creation logs'],
    },
    {
        id: 'T1613',
        name: 'Container and Resource Discovery',
        tacticIds: ['TA0007'],
        description: 'Adversaries may attempt to discover containers and other resources in the environment.',
        mitigations: ['D3-NI', 'D3-ACL'],
        detection: ['API call monitoring', 'Orchestrator audit logs'],
    },
];
/**
 * Helper functions
 */
export function getTacticById(id) {
    return ATTACK_TACTICS.find(t => t.id === id);
}
export function getTechniqueById(id) {
    return ATTACK_TECHNIQUES.find(t => t.id === id);
}
export function getTechniquesByTactic(tacticId) {
    return ATTACK_TECHNIQUES.filter(t => t.tacticIds.includes(tacticId));
}
export function searchTechniques(query) {
    const lowerQuery = query.toLowerCase();
    return ATTACK_TECHNIQUES.filter(t => t.id.toLowerCase().includes(lowerQuery) ||
        t.name.toLowerCase().includes(lowerQuery) ||
        t.description.toLowerCase().includes(lowerQuery));
}
/**
 * Get ATT&CK reference URL
 */
export function getAttackUrl(techniqueId) {
    if (!techniqueId) {
        return 'https://attack.mitre.org/techniques/enterprise/';
    }
    // Handle sub-techniques: T1566.001 -> T1566/001
    return `https://attack.mitre.org/techniques/${techniqueId.replace('.', '/')}/`;
}
